import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { images } from '../data/mockData';
import { getResponsiveImageUrl } from '../lib/imageOptimization';

const ContactPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submission, setSubmission] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    const form = e.currentTarget;
    const formData = new FormData(form);
    setIsSubmitting(true);
    setSubmission(null);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: String(formData.get('first_name') || '').trim(),
          last_name: String(formData.get('last_name') || '').trim(),
          email: String(formData.get('email') || '').trim(),
          message: String(formData.get('message') || '').trim(),
          website: String(formData.get('website') || ''),
        }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result.detail || 'Your message could not be sent. Please try again.');
      }

      form.reset();
      setSubmission({ type: 'success', text: 'Thank you. Your message has been sent.' });
    } catch (error) {
      setSubmission({
        type: 'error',
        text: error.message || 'Your message could not be sent. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Header Section */}
      <section className="py-20 bg-gradient-to-br from-purple-100 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-purple-800 mb-6 font-title">
            Contact Us
          </h1>
          <p className="text-lg text-purple-700 leading-relaxed font-body">
            We are here to help you every step of the way. Thank you for choosing The Wisdom Network!
          </p>
          <p className="text-xl font-semibold text-purple-800 mt-4 font-subtitle">
            The Wisdom Network: Verified Wisdom, Powerful Transformations
          </p>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-16">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-white border border-purple-200 shadow-lg">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="first_name" className="block text-sm font-semibold text-purple-800 mb-2 font-subtitle">
                      First name*
                    </label>
                    <Input
                      id="first_name"
                      name="first_name"
                      type="text"
                      required
                      maxLength={100}
                      className="w-full border-purple-300 focus:border-purple-500 focus:ring-purple-500 font-body"
                      placeholder="Enter your first name"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="last_name" className="block text-sm font-semibold text-purple-800 mb-2 font-subtitle">
                      Last name*
                    </label>
                    <Input
                      id="last_name"
                      name="last_name"
                      type="text"
                      required
                      maxLength={100}
                      className="w-full border-purple-300 focus:border-purple-500 focus:ring-purple-500 font-body"
                      placeholder="Enter your last name"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-purple-800 mb-2 font-subtitle">
                    Email*
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                      maxLength={320}
                    className="w-full border-purple-300 focus:border-purple-500 focus:ring-purple-500 font-body"
                    placeholder="Enter your email address"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-purple-800 mb-2 font-subtitle">
                    Message
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    rows={6}
                    maxLength={5000}
                    className="w-full border-purple-300 focus:border-purple-500 focus:ring-purple-500 resize-none font-body"
                    placeholder="Enter your message here"
                  />
                </div>

                <div
                  aria-hidden="true"
                  className="absolute left-[-10000px] h-px w-px overflow-hidden"
                >
                  <label htmlFor="website">Leave this field empty</label>
                  <input
                    id="website"
                    name="website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>

                {submission && (
                  <p
                    role={submission.type === 'error' ? 'alert' : 'status'}
                    className={`text-center font-body ${
                      submission.type === 'error' ? 'text-red-700' : 'text-green-700'
                    }`}
                  >
                    {submission.text}
                  </p>
                )}

                <div className="text-center">
                  <Button 
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full sm:w-auto bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-8 text-lg rounded-lg transition-all duration-300 transform hover:scale-105 shadow-xl font-body"
                  >
                    {isSubmitting ? 'Sending…' : 'Submit'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Alternative Contact Methods */}
      <section className="py-16 bg-purple-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-purple-800 mb-8 font-title">
            Other Ways to Reach Us
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="bg-white border border-purple-200 hover:border-purple-400 transition-all duration-300">
              <CardContent className="p-8 text-center">
                <h3 className="text-xl font-bold text-purple-800 mb-4 font-subtitle">Email Support</h3>
                <p className="text-purple-700 mb-4 font-body">
                  For general inquiries and support
                </p>
                <a 
                  href="mailto:help@thewisdomnetwork.net?subject=I need assistance with..."
                  className="text-purple-600 hover:text-purple-800 font-semibold underline font-body"
                >
                  help@thewisdomnetwork.net
                </a>
              </CardContent>
            </Card>

            <Card className="bg-white border border-purple-200 hover:border-purple-400 transition-all duration-300">
              <CardContent className="p-8 text-center">
                <h3 className="text-xl font-bold text-purple-800 mb-4 font-subtitle">Book a Session</h3>
                <p className="text-purple-700 mb-4 font-body">
                  Ready to connect with our practitioners?
                </p>
                <Button 
                  onClick={() => window.open('https://thewisdomnetwork.as.me/', '_blank', 'noopener noreferrer')}
                  className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-2 px-6 rounded-lg font-body"
                >
                  Schedule Now
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Bottom Branding Section */}
      <section className="py-16 bg-purple-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-6">
            <img
              src={getResponsiveImageUrl(images.spiritual, { width: 700 })}
              alt="The Wisdom Network"
              className="w-96 h-24 mx-auto rounded-lg object-cover shadow-lg"
              loading="lazy"
              decoding="async"
            />
          </div>

          <h2 className="text-3xl font-bold text-transparent bg-gradient-to-r from-yellow-400 to-yellow-300 bg-clip-text mb-4 font-title">
            THE WISDOM NETWORK:
          </h2>
          
          <p className="text-xl text-yellow-200 font-semibold font-subtitle">
            VERIFIED WISDOM, POWERFUL TRANSFORMATIONS
          </p>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;